import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { SistemaStatusService } from '../services/sistema-status.service';

const EXCLUDED_URLS = [
  '/auth/refresh',
  '/auth/login',
  '/auth/logout',
  's3.amazonaws.com',
  's3.us-east-1.amazonaws.com',
];

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private authService: AuthService,
    private sistemaStatus: SistemaStatusService,
  ) {}

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    if (this.isExcludedUrl(request)) {
      return next.handle(request);
    }

    let token = this.authService.getAccessToken();

    if (!token) {
      this.router.navigate(['/auth/login']);
      return next.handle(request);
    }

    const isExpiringSoon = this.authService.isTokenExpiringSoon(token);
    if (isExpiringSoon) {
      return this.authService.handleRefreshToken(token).pipe(
        switchMap((newToken: string) => {
          this.authService.setAccessToken(newToken);
          token = newToken;

          const authReq = request.clone({
            headers: request.headers.set('Authorization', `Bearer ${token}`),
          });
          return next.handle(authReq);
        }),
        catchError((err) => {
          this.authService.clearSession();
          this.sistemaStatus.reset();
          this.router.navigate(['/auth/login']);
          return throwError(() => err);
        }),
      );
    }

    const authReq = request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          const alreadyRetried = request.headers.has('X-Refresh-Attempted');
          if (!alreadyRetried) {
            return this.authService.handleRefreshToken(token!).pipe(
              switchMap((newToken: string) => {
                this.authService.setAccessToken(newToken);
                const retryReq = request.clone({
                  headers: request.headers
                    .set('Authorization', `Bearer ${newToken}`)
                    .set('X-Refresh-Attempted', 'true'),
                });
                return next.handle(retryReq);
              }),
              catchError((err) => {
                this.authService.clearSession();
                this.sistemaStatus.reset();
                this.router.navigate(['/auth/login']);
                return throwError(() => err);
              }),
            );
          }
          this.authService.clearSession();
          this.sistemaStatus.reset();
          this.router.navigate(['/auth/login']);
          return throwError(() => error);
        }
        return throwError(() => error);
      }),
    );
  }

  private isExcludedUrl(request: HttpRequest<any>): boolean {
    return EXCLUDED_URLS.some((excluded) => request.url.includes(excluded));
  }
}
